import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import { useProject } from "../app/projectContext";
import {
  createSurgeProtectionRun,
  type SurgeProtectionRunCreateRequest,
  type SurgeProtectionRunResponse,
} from "../services/surgeProtection";

/**
 * Surge protection study mutation (EOS-10, A35).
 *
 * Mirrors useCableTray:
 * - One in-flight request at a time: a new submit aborts the previous one.
 * - The in-flight request is aborted on unmount so no state update lands on an unmounted
 *   component.
 * - The run is attached to the revision chosen in the top bar, read at submit time; with no
 *   project, or one without an open revision, nothing is sent and the run is unassigned.
 * - Every calculation is persisted as a run; the typed result and the run summary (with its
 *   fault link and SLD link) are exposed exactly as parsed, NO_SOLUTION included.
 * - A linked fault run must belong to the revision the study is saved in; the backend says so
 *   (409) and the message reaches the page unchanged.
 */
export function useSurgeProtection() {
  const controllerRef = useRef<AbortController | null>(null);
  const { activeRevisionId } = useProject();

  const mutation = useMutation<SurgeProtectionRunResponse, Error, SurgeProtectionRunCreateRequest>({
    mutationKey: ["electrical", "surge-protection", "runs", "create"],
    mutationFn: (payload) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      return createSurgeProtectionRun(payload, controller.signal, activeRevisionId() ?? undefined);
    },
  });

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const calculate = useCallback(
    (payload: SurgeProtectionRunCreateRequest) => mutation.mutateAsync(payload),
    [mutation],
  );

  const reset = useCallback(() => {
    controllerRef.current?.abort();
    mutation.reset();
  }, [mutation]);

  return {
    calculate,
    reset,
    result: mutation.data?.result ?? null,
    run: mutation.data?.run ?? null,
    error: mutation.error,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export type UseSurgeProtectionResult = ReturnType<typeof useSurgeProtection>;
